import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import SelectInput from "../../../../../components/inputs/select";
import TextInput from "../../../../../components/inputs/text";
import NumberInput from "../../../../../components/inputs/number";
import CheckboxInput from "../../../../../components/inputs/check";
import { BtnSubmit } from "../../../../../components/button/button-submit/button-submit";
import MessageDialog from "../../../../../components/ui/dialog/MessageDialog";
import Loading from "../../../../../components/loading";
import reqEmpresaItens from "../../../../../services/reqMultiTenant/reqEmpresaItens";
import reqProduto from "../../../../../services/reqMultiTenant/reqProduto";
import functions from "../../../../../util/functions";
import LibDateTime from "../../../../../util/libDateTime";
import { schema, defaultValues, STATUS_MODULO, STATUS_COBRADO } from "./schema";

const COR_STATUS = {
	ATIVO: "bg-green-500",
	EXPIRADO: "bg-red-500",
	DEMO: "bg-indigo-500",
	DEMO_EXPIRADO: "bg-slate-400",
	FREE: "bg-sky-500",
};

const OPCOES_STATUS = STATUS_MODULO.map((status) => ({ status }));

const paraFormulario = (item) => ({
	id_empresa_itens: item.id_empresa_itens,
	id_produto: item.id_produto,
	status: item.status,
	data_expiracao: item.data_expiracao?.slice(0, 10) ?? "",
	desconto: item.desconto ?? 0,
	acrescimo: item.acrescimo ?? 0,
	obs: item.obs ?? "",
	autorizado: Boolean(item.autorizado),
});

export const AbaModulos = ({ idEmpresa }) => {
	const {
		handleSubmit,
		reset,
		watch,
		control,
		register,
		formState: { errors, isSubmitting },
	} = useForm({
		mode: "onSubmit",
		resolver: zodResolver(schema),
		defaultValues,
	});

	const [modulos, setModulos] = useState([]);
	const [produtos, setProdutos] = useState([]);
	const [loading, setLoading] = useState(false);
	const [itemExcluir, setItemExcluir] = useState(null);

	const idEmEdicao = watch("id_empresa_itens");

	const totalMensal = modulos
		.filter((item) => STATUS_COBRADO.includes(item.status))
		.reduce((soma, item) => soma + Number(item.valor_final), 0);

	const carregarModulos = async () => {
		try {
			const { data } = await reqEmpresaItens.listModulos(idEmpresa);
			setModulos(data);
		} catch (error) {
			toast.error("Erro ao carregar os módulos: " + error.message);
		}
	};

	useEffect(() => {
		const carregar = async () => {
			try {
				setLoading(true);
				const [{ data: listaProdutos }] = await Promise.all([reqProduto.listProductsSZ(), carregarModulos()]);
				setProdutos(listaProdutos.map((p) => ({ ...p, rotulo: `${p.nome} · ${functions.strToCurrency(p.valor)}` })));
			} catch (error) {
				toast.error("Erro ao carregar os produtos: " + error.message);
			} finally {
				setLoading(false);
			}
		};

		carregar();
	}, []);

	const handleOnSubmit = async (data) => {
		try {
			await reqEmpresaItens.saveModulo(idEmpresa, data);
			toast.success(data.id_empresa_itens ? "Módulo alterado." : "Módulo incluído.");
			reset(defaultValues);
			carregarModulos();
		} catch (error) {
			toast.error(error.message);
		}
	};

	const handleExcluir = async () => {
		try {
			await reqEmpresaItens.removeModulo(idEmpresa, itemExcluir.id_empresa_itens);
			toast.success("Módulo excluído.");
			if (idEmEdicao === itemExcluir.id_empresa_itens) reset(defaultValues);
			carregarModulos();
		} catch (error) {
			toast.error(error.message);
		} finally {
			setItemExcluir(null);
		}
	};

	if (loading) {
		return (
			<div className="flex h-44 items-center justify-center">
				<Loading mensagem="Carregando módulos..." />
			</div>
		);
	}

	return (
		<div className="space-y-3 animate-in slide-in-from-right-2 duration-200">
			<div className="bg-white rounded-lg border shadow-inner overflow-auto max-h-64">
				<table className="w-full text-xs">
					<thead className="bg-slate-50 text-slate-500 sticky top-0">
						<tr>
							<th className="p-2 text-left">Produto</th>
							<th className="p-2 text-right">Tabela</th>
							<th className="p-2 text-right">Desc. %</th>
							<th className="p-2 text-right">Acrés. %</th>
							<th className="p-2 text-right">Valor</th>
							<th className="p-2 text-center">Status</th>
							<th className="p-2 text-center">Expira</th>
							<th className="p-2" />
						</tr>
					</thead>
					<tbody>
						{modulos.map((item) => (
							<tr
								key={item.id_empresa_itens}
								className={`border-t ${idEmEdicao === item.id_empresa_itens ? "bg-amber-50" : ""}`}
							>
								<td className="p-2">
									<strong>{item.produto}</strong>
									<span className="block text-[10px] text-slate-400">{item.modulo}</span>
								</td>
								<td className="p-2 text-right">{functions.strToCurrency(item.valor)}</td>
								<td className="p-2 text-right">{item.desconto}</td>
								<td className="p-2 text-right">{item.acrescimo}</td>
								<td className="p-2 text-right font-semibold">{functions.strToCurrency(item.valor_final)}</td>
								<td className="p-2 text-center">
									<span
										className={`px-2 py-0.5 rounded text-white text-[10px] font-bold ${COR_STATUS[item.status] ?? "bg-slate-400"}`}
									>
										{item.status}
									</span>
								</td>
								<td className="p-2 text-center">{LibDateTime.parseDelphiLocal(item.data_expiracao, "dd/MM/yyyy")}</td>
								<td className="p-2">
									<div className="flex justify-end gap-1">
										<button
											type="button"
											className="btn btn-sm btn-outline-primary"
											onClick={() => reset(paraFormulario(item))}
										>
											<FontAwesomeIcon icon={faPen} />
										</button>
										<button
											type="button"
											className="btn btn-sm btn-outline-danger"
											onClick={() => setItemExcluir(item)}
										>
											<FontAwesomeIcon icon={faTrash} />
										</button>
									</div>
								</td>
							</tr>
						))}
						{modulos.length === 0 && (
							<tr>
								<td colSpan={8} className="p-4 text-center italic text-slate-400">
									Nenhum módulo contratado.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<div className="flex justify-between items-center text-xs">
				<span className="text-slate-400">Entram na fatura os itens ATIVO e EXPIRADO</span>
				<strong>Total mensal: {functions.strToCurrency(totalMensal)}</strong>
			</div>

			<form className="bg-white p-3 rounded-lg border shadow-sm space-y-2" onSubmit={handleSubmit(handleOnSubmit)}>
				<h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider border-b pb-1">
					{idEmEdicao ? "Alterar módulo" : "Incluir módulo"}
				</h3>

				<div className="grid grid-cols-2 gap-x-3">
					<SelectInput
						id="id_produto"
						label="Produto"
						labelSelect="rotulo"
						control={control}
						data={produtos}
						errors={errors}
						className="col-span-2"
					/>
					<SelectInput
						id="status"
						label="Status"
						labelSelect="status"
						control={control}
						data={OPCOES_STATUS}
						errors={errors}
					/>
					<div className="input-box">
						<label htmlFor="data_expiracao">Expira em</label>
						<input
							id="data_expiracao"
							type="date"
							className={errors.data_expiracao ? "error" : ""}
							{...register("data_expiracao")}
						/>
						{errors.data_expiracao && <p className="errors">{errors.data_expiracao.message}</p>}
					</div>
					<NumberInput label="Desconto %" name="desconto" control={control} errors={errors} decimalScale={2} />
					<NumberInput label="Acréscimo %" name="acrescimo" control={control} errors={errors} decimalScale={2} />
					<TextInput label="Observação" id="obs" register={register} errors={errors} className="col-span-2" />
					<CheckboxInput
						label="Autorizado (a geração da fatura marca sozinha)"
						id="autorizado"
						register={register}
						errors={errors}
						className="col-span-2"
					/>
				</div>

				<div className="flex justify-end gap-2 pt-2">
					{idEmEdicao > 0 && (
						<button type="button" className="btn btn-outline-secondary" onClick={() => reset(defaultValues)}>
							Cancelar
						</button>
					)}
					<BtnSubmit text={idEmEdicao ? "Salvar alteração" : "Incluir"} isSubmitting={isSubmitting} className="w-40" />
				</div>
			</form>

			<MessageDialog
				open={Boolean(itemExcluir)}
				variant="warning"
				buttons="confirmCancel"
				autoFocus="cancel"
				title={`Excluir ${itemExcluir?.produto ?? ""}`}
				description="O módulo sai do menu do cliente e da próxima fatura."
				onConfirm={handleExcluir}
				onCancel={() => setItemExcluir(null)}
			/>
		</div>
	);
};

export default AbaModulos;
